/**
 * TanStack Query hooks for the approvals endpoint group
 * (apps/api/flow_api/routers/approvals.py). Operator and above.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/apiClient";

/** One row of `GET /api/approvals`, as `_approval_to_dict` sends it. */
export interface ApprovalItem {
  /** The approval's own id. Not the job id. */
  id: string;
  jobId: string;
  kind: string;
  /** `pending`, `approved`, `denied` or `expired`. */
  state: string;
  expiresAt: string | null;
  requestedAt: string;
  /** The runner's description of the action it is about to take. */
  notes: string;
  /** Phase the runner is paused in. Null when the row carries none. */
  phase: { id: string; order: number; name: string; riskLevel: string } | null;
  workflow: { id: string; name: string };
  entity: { id: string; full: string };
}

interface ApprovalsResponse {
  items: ApprovalItem[];
  total: number;
}

export function useApprovalsApi() {
  return useQuery({
    queryKey: ["approvals"],
    queryFn: () => apiFetch<ApprovalsResponse>("/approvals"),
    refetchInterval: 5_000,
    staleTime: 2_000,
  });
}

interface ResolveResponse {
  id: string;
  decision: "approved" | "denied";
  resolvedBy: string;
}

export function useResolveApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      decision,
      notes,
    }: {
      id: string;
      decision: "approved" | "denied";
      notes?: string;
    }) =>
      apiFetch<ResolveResponse>(`/approvals/${id}/${decision === "approved" ? "approve" : "deny"}`, {
        method: "POST",
        body: JSON.stringify({ notes: notes ?? null }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["approvals"] });
      qc.invalidateQueries({ queryKey: ["jobs"] });
    },
  });
}
